import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { AppRoutes } from '@/constants';
import { ErrorBoundary } from './ErrorBoundary';
import { ErrorFallback } from './ErrorFallback';

/** Outermost boundary. Takes the viewport, but the app shell stays mounted and a route change clears it. */
export const RouteErrorBoundary = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  return (
    <ErrorBoundary
      name="route"
      resetKey={pathname}
      fallback={({ onRetry }) => (
        <div className="flex h-screen w-full items-center justify-center">
          <ErrorFallback
            title="Something went wrong"
            description="This page ran into an unexpected problem. Trying again usually fixes it — if it keeps happening, please let us know what you were doing."
            primaryAction={{ label: 'Try again', onClick: onRetry }}
            secondaryAction={{
              label: 'Back to chat',
              onClick: () => {
                onRetry();
                navigate(`/${AppRoutes.Chat}`);
              },
            }}
          />
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
};
